import { Component, signal } from '@angular/core';
import { AngularModule } from '../../../../modules/angular.module';
import { CourseService } from '../../../../services/course.service';
import { NavigationService } from '../../../../services/navigation.service';
import { AdminService } from '../../../../services/admin.service';
import { Course, CourseText, Engines, LanguageCode, Product } from '@mira-avant/mtlibs';
import { ZorroModule } from '../../../../modules/zorro.module';
import { CourseFormComponent } from '../../../forms/standard/course-form/course-form.component';
import { LanguageSelectorComponent } from '../../language-selector/language-selector.component';
import { v4 } from 'uuid';
import { ClipboardService } from '../../../../services/clipboard.service';
import { Subscription } from 'rxjs';
import { EMPTY_COURSE, _ } from '@mira-avant/mtlibs';
import { InProgressService } from '../../../../services/in-progress.service';
import { RubricContentLevelsReportComponent } from '../rubric-content-levels-report/rubric-content-levels-report.component';
import { TableFormContainer } from '../table-form-container/table-form-container';
import {
	CmsTable,
	Column,
	ENABLE_COPY_COLUMN,
	ENABLE_DELETE_COLUMN,
	ENABLE_EDIT_COLUMN,
	ENABLE_DISPLAY_ORDER_COLUMN,
	ENABLE_UP_DOWN_COLUMN,
	ENABLE_CHECKBOX_COLUMN,
} from '../cms-table/cms-table';
import { RowColumn } from '../cms-table/row-column';
import { HeaderColumn } from '../cms-table/header-column';
import { AuthService } from '../../../../services/auth.service';

@Component({
	selector: 'app-course-list',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		CourseFormComponent,
		LanguageSelectorComponent,
		RubricContentLevelsReportComponent,
		TableFormContainer,
		CmsTable,
		RowColumn,
		HeaderColumn,
	],
	templateUrl: './course-list.component.html',
	styleUrl: './course-list.component.scss',
})
export class CourseListComponent {
	readonly loading = signal<boolean>(true);
	readonly courses = signal<Course[]>([]);
	readonly uuidsInProgress = signal<string[]>([]);
	readonly editLanguage = signal<string>(LanguageCode.EN);
	readonly editCourse = signal<Course | undefined>(undefined);
	readonly isStrideCourse = signal<boolean>(false);
	readonly columns = signal<Column[]>([]);
	readonly isAdmin = signal<boolean>(false);
	readonly isFormShowing = signal<boolean>(false);

	private editCourseProduct: Product | undefined;
	private sub: Subscription | undefined;

	constructor(
		public courseService: CourseService,
		private authService: AuthService,
		public nav: NavigationService,
		private adminService: AdminService,
		public clipboardService: ClipboardService,
		private inProgressService: InProgressService
	) {
		this.sub = this.courseService.courses$.subscribe((courses: Course[]) => {
			this.courses.set(courses);
			this.updateTableDetails();

			const courseUuids: string[] = courses.map(course => course.uuid);

			this.inProgressService.coursesInProgress$(courseUuids).then((uuids: string[]) => {
				this.uuidsInProgress.set(uuids);
			});

			if (this.editCourse()) {
				this.courseService.getProduct$(this.editCourse()!.uuid).then(async (courseProduct: Product) => {
					this.editCourseProduct = courseProduct;
					this.isStrideCourse.set(courseProduct.engine_name === Engines.STRIDE);
				});
			}

			this.loading.set(false);
		});

		this.authService.isAdmin$().then((isAdmin: boolean) => {
			this.isAdmin.set(isAdmin);
			this.updateTableDetails();
		});
	}

	private updateTableDetails(): void {
		const columns: Column[] = [];

		if (this.isAdmin()) {
			columns.push(ENABLE_CHECKBOX_COLUMN);
		}

		columns.push(ENABLE_DISPLAY_ORDER_COLUMN, {
			key: 'courseName',
			name: 'Name',
			sortOrder: null,
			sortFn: (a: Course, b: Course) => a.active_text?.name.localeCompare(b.active_text?.name || '') || 0,
			sortDirections: ['ascend', 'descend', null],
			filterMultiple: true,
			filterOptions: this.courses().map(row => ({
				text: row.active_text?.name || '',
				value: row.active_text?.name || '',
			})),
			filterFn: (list: string[], item: Course) => list.some(name => item.active_text?.name.indexOf(name) !== -1),
		});

		if (this.isAdmin()) {
			columns.push(ENABLE_COPY_COLUMN);
			columns.push(ENABLE_UP_DOWN_COLUMN);
			columns.push(ENABLE_EDIT_COLUMN);
			columns.push(ENABLE_DELETE_COLUMN);
		}

		this.columns.set(columns);
	}

	ngOnInit(): void {
		this.loading.set(true);
		this.reloadCourses$()
			.catch(() => {
				console.warn('no courses found');
			})
			.finally(() => {
				this.loading.set(false);
			});
	}

	public select($event: unknown): void {
		const course = $event as Course;

		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}

		this.nav.navigate([
			'my',
			'admin',
			'lc',
			{
				clientUuid: this.adminService.selectedClient$.value.uuid,
				courseUuid: course.uuid,
			},
		]);
	}

	public copy($event: object): void {
		const course = $event as Course;
		this.clipboardService.copyCourse(course);
	}

	public copyBulk($event: object[]): void {
		const courses = $event as Course[];
		this.clipboardService.copyCourses(courses);
	}

	public edit($event: unknown): void {
		const course = $event as Course;

		this.isStrideCourse.set(false);
		this.editCourse.set(this.editCourse()?.uuid === course.uuid ? undefined : course);
		this.isFormShowing.set(this.editCourse() !== undefined);

		if (this.editCourse()) {
			this.courseService.getProduct$(this.editCourse()!.uuid).then(async (courseProduct: Product) => {
				this.isStrideCourse.set(courseProduct.engine_name === Engines.STRIDE);
			});
		}
	}

	public onShowForm(): void {
		this.isFormShowing.set(true);
	}

	public onHideForm(): void {
		this.isFormShowing.set(false);
		this.resetEditState();
	}

	private resetEditState(): void {
		this.editCourse.set(undefined);
		this.editLanguage.set(LanguageCode.EN);
	}

	public async delete$($event: unknown): Promise<void> {
		const course = $event as Course;

		this.loading.set(true);
		try {
			await this.courseService.delete$(course.uuid);
			this.courses.set(this.courses().filter(c => c.uuid !== course.uuid));
		} catch (error) {
			console.error('Error deleting course:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async moveUp$($event: unknown): Promise<void> {
		const course = $event as Course;

		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}
		this.loading.set(true);
		try {
			const updatedCourses = await this.courseService.moveUp$(
				course.uuid,
				this.adminService.selectedClient$.value.uuid
			);
			this.courses.set(updatedCourses);
		} catch (error) {
			console.error('Error moving course up:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async moveDown$($event: unknown): Promise<void> {
		const course = $event as Course;

		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}
		this.loading.set(true);
		try {
			const updatedCourses = await this.courseService.moveDown$(
				course.uuid,
				this.adminService.selectedClient$.value.uuid
			);
			this.courses.set(updatedCourses);
		} catch (error) {
			console.error('Error moving course down:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async onCourseFormSubmit$($event: unknown): Promise<void> {
		let submitSucceeded = false;
		this.loading.set(true);
		try {
			if (this.editCourse()) {
				await this.updateCourse$($event);
			} else {
				await this.createNewCourse$($event);
			}

			await this.reloadCourses$();
			submitSucceeded = true;
		} catch (error) {
			console.error('Error submitting course form:', error);
		} finally {
			this.loading.set(false);
			this.resetEditState();

			if (submitSucceeded) {
				this.isFormShowing.set(false);
			}
		}
	}

	private async reloadCourses$(): Promise<void> {
		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}

		const courses: Course[] = await this.courseService.getByClient$(this.adminService.selectedClient$.value.uuid);
		this.courses.set(courses);
		this.updateTableDetails();

		const currentEditCourse = this.editCourse();
		if (currentEditCourse) {
			const refreshedEditCourse = courses.find(course => course.uuid === currentEditCourse.uuid);
			this.editCourse.set(refreshedEditCourse);
		}
	}

	private async createNewCourse$($event: any): Promise<void> {
		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}

		await this.courseService.create$({
			client_uuid: this.adminService.selectedClient$.value.uuid,
			default_lang_id: LanguageCode.EN,
			data_json: {},
			product_name: $event.productName || 'Journey',
			rubric_code: $event.rubricCode || undefined,
			sso_course_id: $event.ssoCourseId || undefined,
			course_text: [
				{
					default_lang_id: LanguageCode.EN,
					name: $event.name,
					description_short: $event.descriptionShort,
					description_html: $event.descriptionHtml,
				},
			],
		});
	}

	private async updateCourse$($event: any): Promise<void> {
		const currentEditCourse = this.editCourse();
		if (!currentEditCourse) {
			throw new Error('No course to update');
		}

		const courseTexts: CourseText[] = currentEditCourse.course_text!;

		let courseText: CourseText | undefined = courseTexts.find(text => text.default_lang_id === this.editLanguage());

		if (!courseText) {
			courseText = {
				uuid: v4(),
				course_uuid: currentEditCourse.uuid,
				default_lang_id: this.editLanguage(),
				name: $event.name,
				description_short: $event.descriptionShort,
				description_html: $event.descriptionHtml,
			};

			courseTexts.push(courseText);
		} else {
			courseText.name = $event.name;
			courseText.description_short = $event.descriptionShort;
			courseText.description_html = $event.descriptionHtml;
		}

		const props = Object.keys(EMPTY_COURSE);
		const payload: Course = _.pick(currentEditCourse, props) as Course;
		payload.data_json = $event.dataJson ? JSON.parse($event.dataJson) : {};
		payload.product_name = $event.productName || Engines.JOURNEY;
		payload.rubric_code = $event.rubricCode || undefined;
		payload.sso_course_id = $event.ssoCourseId || undefined;

		await this.courseService.update$(currentEditCourse.uuid, {
			...payload,
			course_text: courseTexts,
		});
	}

	public onEditLanguageChange(code: string): void {
		this.editLanguage.set(code);
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
